import React, { ReactNode, useState } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { StringParam, useQueryParam } from 'use-query-params';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
} from '@mui/material';
import {
    Business, ChevronLeft,
    ChevronRight, Delete, MoreVert,
    Restore, WarningAmber
} from '@mui/icons-material';
import ShareButton from 'components/common/ShareButton';
import useContextMenu from 'hooks/useContextMenu';
import { CarModel, StockStatus } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import { CarFormFields } from 'modules/cars/common/CarFormFields';
import useCarsFilter from 'modules/cars/hooks/useCarsFilter';
import useCarModals from 'modules/cars/hooks/useCarModals';
import useCars from 'modules/cars/hooks/useCars';
import auth from 'recoil/atoms/auth';


type CarCardProps = {
    car: CarModel,
    title: ReactNode,
}

const CarCard = ({car: init, title}: CarCardProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const navigate = useNavigate();
    const [docPreview] = useQueryParam('doc', StringParam);
    const [car, setCar] = useState<CarModel>({
        ...init,
        referenceNum: init.exportCertificate?.referenceNum,
        validUntil: init.exportCertificate?.validUntil ? DateTime.fromISO(init.exportCertificate.validUntil) : null
    });
    const {updateCar} = useCars();
    const {enqueueSnackbar} = useSnackbar();
    const {
        confirmCancelShipping,
        confirmRestoreCar,
        confirmRemoveCar,
        openChangeChassisModal,
        openChangeShipperModal,
    } = useCarModals();
    const {cars} = useCarsFilter(true);
    const location = useLocation();

    const nextCar = () => moveTo(idx => idx === cars.length - 1 ? 0 : idx + 1);
    const prevCar = () => moveTo(idx => idx === 0 ? cars.length - 1 : idx - 1);

    const moveTo = (func: (idx: number) => number) => {
        const sorted = cars
            .sort((a, b) =>
                ((a.yard?.portId || 0) - (b.yard?.portId || 0)) ||
                ((a.yard?.id || 0) - (b.yard?.id || 0)) ||
                (a.company.id - b.company.id) ||
                (b.id - a.id)
            );
        let idx = sorted.findIndex(c => c.id === init.id);
        const next = cars[idx > -1 ? func(idx) : 0];
        navigate(`/car/${next.id}${location.search}`)
    }

    const showMore = my.permissions.has('cars manage essentials')
        || (car.status === StockStatus.Canceled ? my.permissions.has('cars manage') : my.permissions.has('cars cancel'))
        || my.permissions.has('cars delete');

    const {openMenu: openManage} = useContextMenu<CarModel>(({value, close}) => [
        my.permissions.has('cars manage essentials') && (
            <MenuItem
                key="chassis"
                onClick={() => {
                    openChangeChassisModal(value);
                    close();
                }}
            >
                <ListItemIcon>
                    <WarningAmber fontSize="small"/>
                </ListItemIcon>
                <ListItemText>{t('change_chassis')}</ListItemText>
            </MenuItem>
        ),

        my.permissions.has('cars manage essentials') && (
            <MenuItem
                key="shipper"
                onClick={() => {
                    openChangeShipperModal(value);
                    close();
                }}
            >
                <ListItemIcon>
                    <Business fontSize="small"/>
                </ListItemIcon>
                <ListItemText>{t('change_shipper')}</ListItemText>
            </MenuItem>
        ),

        value.status === StockStatus.Canceled ? (
            my.permissions.has('cars manage') && (
                <MenuItem
                    key="restore"
                    onClick={() => {
                        confirmRestoreCar(value);
                        close();
                    }}
                >
                    <ListItemIcon>
                        <Restore fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>{t('restore')}</ListItemText>
                </MenuItem>
            )
        ) : (
            my.permissions.has('cars cancel') && (
                <MenuItem
                    key="cancel"
                    onClick={() => {
                        confirmCancelShipping(value);
                        close();
                    }}
                >
                    <ListItemIcon>
                        <Delete fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>{t('cancel_shipping')}</ListItemText>
                </MenuItem>
            )
        ),

        my.permissions.has('cars delete') && (
            <MenuItem
                key="delete"
                onClick={() => {
                    confirmRemoveCar(value).then(() => navigate('/cars/pending'));
                    close();
                }}
            >
                <ListItemIcon>
                    <Delete fontSize="small"/>
                </ListItemIcon>
                <ListItemText>{t('delete_forever')}</ListItemText>
            </MenuItem>
        ),
    ]);

    const change = (k: string, v: any) => setCar(old => ({...old!, [k]: v}));

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const temp = {...car};

        if (temp.validUntil instanceof DateTime)
            temp.validUntil = temp.validUntil.toISODate();

        updateCar(temp)
            .then(() => enqueueSnackbar(t('saved'), {variant: 'success'}))
    };

    const canAction = car.status === StockStatus.Pending || my.type === CompanyType.Master;

    const actions = <>
        <IconButton
            aria-label="prev car"
            onClick={prevCar}
        >
            <ChevronLeft/>
        </IconButton>

        <IconButton
            aria-label="next car"
            onClick={nextCar}
        >
            <ChevronRight/>
        </IconButton>

        <ShareButton
            title={`${car.make} ${car.model} ${car.chassisNumber}`}
            url={`${process.env.REACT_APP_ORIGIN}/cars/${car.chassisNumber}`}
        />

        {showMore && (
            <IconButton
                aria-label="manage car"
                onClick={e => openManage(e, car)}
            >
                <MoreVert/>
            </IconButton>
        )}
    </>;

    return (
        <Card sx={{mb: 2}}>
            <CardHeader
                title={title}
                action={actions}
            />
            <Box component="form" onSubmit={submit}>
                <CardContent>
                    <CarFormFields
                        car={car}
                        onChange={change}
                        docPreview={docPreview}
                    />
                </CardContent>

                <CardActions sx={{justifyContent: 'flex-end'}}>
                    {canAction && <Button type="submit" size="small">{t('save')}</Button>}
                </CardActions>
            </Box>
        </Card>
    )
};

export default CarCard;
